// TelcoTrack — vnořené řádky souhrnu materiálů (kategorie stromu katalogu → SAP).

import type { MaterialsPlanBySap } from './materials-plan-fetch.util';

/** Pořadí kategorií odpovídá `components` tabulce / GET components/tree. */
export const MATERIAL_TREE_CATEGORY_KEYS = [
  'mainCategory',
  'subCategory',
  'branch1',
  'branch2',
  'branch3',
  'branch4',
] as const;

export type MaterialTreeCategoryKey = (typeof MATERIAL_TREE_CATEGORY_KEYS)[number];

export interface MaterialCategoryPathFields {
  mainCategory?: string | null;
  subCategory?: string | null;
  branch1?: string | null;
  branch2?: string | null;
  branch3?: string | null;
  branch4?: string | null;
}

export interface MaterialSummarySourceRow extends MaterialCategoryPathFields {
  sapCode: string;
  name: string;
  quantity: number;
  lineTotal: number | null;
  unit: string | null;
}

export interface MaterialSummarySapLine {
  sapCode: string;
  name: string;
  quantity: number;
  lineTotal: number;
  unit: string | null;
  categoryPath: string[];
  sourceRowCount: number;
}

export interface MaterialsSummaryTableRow {
  id: string;
  label: string;
  depth: number;
  rowType: 'category' | 'line' | 'total';
  expandable?: boolean;
  /** Skutečné množství z výkazu materiálu. */
  quantity?: number;
  unit?: string | null;
  lineTotal: number;
  /** PPD plánované množství — jen když `includePlan`. */
  plannedQuantity?: number;
  /** Jednotka plánovaného množství (PPD). */
  plannedUnit?: string | null;
  /** Plán − skutečnost (množství) — jen při shodné jednotce a `includePlan`. */
  variance?: number;
  sapCode?: string;
  sourceRowCount?: number;
}

const UNCATEGORIZED_LABEL = 'Bez kategorie';

function normalizeCategoryValue(value: string | null | undefined): string | null {
  if (value == null || typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  return trimmed.replace(/\s+/g, ' ');
}

export function resolveMaterialCategoryPath(fields: MaterialCategoryPathFields): string[] {
  const path: string[] = [];
  for (const key of MATERIAL_TREE_CATEGORY_KEYS) {
    const segment = normalizeCategoryValue(fields[key]);
    if (segment) path.push(segment);
  }
  return path.length > 0 ? path : [UNCATEGORIZED_LABEL];
}

function roundMoney(value: number): number {
  return Math.round(value * 100) / 100;
}

function roundQuantity(value: number): number {
  return Math.round(value * 10000) / 10000;
}

function aggregateBySap(rows: readonly MaterialSummarySourceRow[]): MaterialSummarySapLine[] {
  const map = new Map<string, MaterialSummarySapLine>();
  for (const row of rows) {
    const key = (row.sapCode || '').trim() || '—';
    const current =
      map.get(key) ??
      ({
        sapCode: key,
        name: row.name || key,
        quantity: 0,
        lineTotal: 0,
        unit: row.unit,
        categoryPath: resolveMaterialCategoryPath(row),
        sourceRowCount: 0,
      } as MaterialSummarySapLine);
    current.quantity += Number(row.quantity) || 0;
    current.lineTotal += Number(row.lineTotal) || 0;
    if (!current.name && row.name) current.name = row.name;
    if (!current.unit && row.unit) current.unit = row.unit;
    current.sourceRowCount += 1;
    map.set(key, current);
  }
  return Array.from(map.values()).sort((a, b) => b.lineTotal - a.lineTotal);
}

interface SummaryTreeNode {
  label: string;
  children: Map<string, SummaryTreeNode>;
  lines: MaterialSummarySapLine[];
}

function getOrCreateChild(parent: SummaryTreeNode, label: string): SummaryTreeNode {
  let child = parent.children.get(label);
  if (!child) {
    child = { label, children: new Map(), lines: [] };
    parent.children.set(label, child);
  }
  return child;
}

function insertLineIntoTree(root: SummaryTreeNode, line: MaterialSummarySapLine): void {
  let node = root;
  for (const segment of line.categoryPath) {
    node = getOrCreateChild(node, segment);
  }
  node.lines.push(line);
}

function collectDescendantLines(node: SummaryTreeNode): MaterialSummarySapLine[] {
  const out = [...node.lines];
  for (const child of node.children.values()) {
    out.push(...collectDescendantLines(child));
  }
  return out;
}

function quantityVarianceFields(
  plannedQuantity: number | undefined,
  quantity: number | undefined,
  plannedUnit: string | null | undefined,
  unit: string | null | undefined,
): Pick<MaterialsSummaryTableRow, 'variance'> {
  if (plannedQuantity == null || quantity == null) return {};
  const plannedNorm = plannedUnit?.trim() || null;
  const actualNorm = unit?.trim() || null;
  if (plannedNorm !== actualNorm) return {};
  return { variance: roundQuantity(plannedQuantity - quantity) };
}

function planQuantityFields(
  plan: { plannedQuantity: number; unit: string | null } | undefined,
): Pick<MaterialsSummaryTableRow, 'plannedQuantity' | 'plannedUnit'> {
  if (!plan) return {};
  return {
    plannedQuantity: roundQuantity(plan.plannedQuantity),
    plannedUnit: plan.unit,
  };
}

function aggregateActualQuantities(
  lines: readonly MaterialSummarySapLine[],
): Pick<MaterialsSummaryTableRow, 'quantity' | 'unit'> | null {
  if (lines.length === 0) return null;
  const units = new Set(lines.map((line) => line.unit?.trim() || null));
  if (units.size !== 1) return null;
  const unit = [...units][0] ?? null;
  let quantity = 0;
  for (const line of lines) {
    quantity += Number(line.quantity) || 0;
  }
  return { quantity: roundQuantity(quantity), unit };
}

/** Součet plánovaných množství podřádků — jen pokud mají stejnou jednotku (PPD). */
function aggregatePlannedQuantities(
  lines: readonly MaterialSummarySapLine[],
  planBySap: MaterialsPlanBySap,
): Pick<MaterialsSummaryTableRow, 'plannedQuantity' | 'plannedUnit'> | null {
  if (lines.length === 0) return null;

  const units = new Set<string | null>();
  let plannedQuantity = 0;
  let hasPlan = false;
  for (const line of lines) {
    const plan = planBySap.get(line.sapCode);
    if (!plan) continue;
    units.add(plan.unit?.trim() || null);
    plannedQuantity += Number(plan.plannedQuantity) || 0;
    hasPlan = true;
  }
  if (!hasPlan) return null;
  if (units.size !== 1) return null;

  return {
    plannedQuantity: roundQuantity(plannedQuantity),
    plannedUnit: [...units][0] ?? null,
  };
}

interface CategoryAppendTotals {
  actualTotal: number;
}

function appendCategoryRows(
  rows: MaterialsSummaryTableRow[],
  node: SummaryTreeNode,
  depth: number,
  idPrefix: string,
  expandedCategoryIds: ReadonlySet<string>,
  includePlan: boolean,
  planBySap: MaterialsPlanBySap,
): CategoryAppendTotals {
  let actualTotal = 0;
  const childEntries = Array.from(node.children.entries()).sort((a, b) =>
    a[0].localeCompare(b[0], 'cs'),
  );

  for (const [label, child] of childEntries) {
    const descendants = collectDescendantLines(child);
    const lineTotal = roundMoney(descendants.reduce((sum, line) => sum + line.lineTotal, 0));
    if (lineTotal === 0 && descendants.length === 0) continue;

    const rowId = `${idPrefix}-${label}`;
    const actualQty = aggregateActualQuantities(descendants);
    const plannedQty = includePlan ? aggregatePlannedQuantities(descendants, planBySap) : null;
    const hasNestedCategories = child.children.size > 0;
    const hasLines = child.lines.length > 0;

    rows.push({
      id: rowId,
      label,
      depth,
      rowType: 'category',
      expandable: hasNestedCategories || hasLines,
      lineTotal,
      ...(includePlan ? (plannedQty ?? {}) : {}),
      ...(actualQty ?? {}),
      ...(includePlan && plannedQty && actualQty
        ? quantityVarianceFields(
            plannedQty.plannedQuantity,
            actualQty.quantity,
            plannedQty.plannedUnit,
            actualQty.unit,
          )
        : {}),
    });
    actualTotal += lineTotal;

    if (!expandedCategoryIds.has(rowId)) continue;

    if (hasNestedCategories) {
      appendCategoryRows(rows, child, depth + 1, rowId, expandedCategoryIds, includePlan, planBySap);
    }

    const sortedLines = [...child.lines].sort((a, b) => b.lineTotal - a.lineTotal);
    for (const line of sortedLines) {
      const actual = roundMoney(line.lineTotal);
      const plan = includePlan ? planBySap.get(line.sapCode) : undefined;
      const quantity = roundQuantity(line.quantity);
      rows.push({
        id: `summary-line-${rowId}-${line.sapCode}`,
        label: line.name?.trim() || line.sapCode,
        sapCode: line.sapCode,
        depth: depth + (hasNestedCategories ? 1 : 1),
        rowType: 'line',
        quantity,
        unit: line.unit,
        lineTotal: actual,
        ...(includePlan ? planQuantityFields(plan) : {}),
        ...(includePlan
          ? quantityVarianceFields(plan?.plannedQuantity, quantity, plan?.unit, line.unit)
          : {}),
        sourceRowCount: line.sourceRowCount,
      });
    }
  }

  return { actualTotal };
}

export function buildMaterialsSummaryTableRows(args: {
  sourceRows: readonly MaterialSummarySourceRow[];
  expandedCategoryIds: ReadonlySet<string>;
  /** PPD plán po SAP — z `GET finance-comparison/material-lines`. */
  planBySap?: MaterialsPlanBySap;
  /** Zobrazit sloupce plán vs skutečnost množství (typicky `hasPpd` z API). */
  includePlan?: boolean;
}): MaterialsSummaryTableRow[] {
  const includePlan = args.includePlan === true && !!args.planBySap;
  const planBySap = args.planBySap ?? new Map<string, never>();

  const sapLines = aggregateBySap(args.sourceRows);
  if (sapLines.length === 0) return [];

  const root: SummaryTreeNode = { label: '', children: new Map(), lines: [] };
  for (const line of sapLines) {
    insertLineIntoTree(root, line);
  }

  const rows: MaterialsSummaryTableRow[] = [];
  const totals = appendCategoryRows(
    rows,
    root,
    0,
    'summary-cat',
    args.expandedCategoryIds,
    includePlan,
    planBySap,
  );

  rows.push({
    id: 'summary-total',
    label: 'Celkem',
    depth: 0,
    rowType: 'total',
    lineTotal: roundMoney(totals.actualTotal),
  });

  return rows;
}
